import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Flex, Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import TriggerRulesList, { type WorkflowRecord } from "./TriggerRulesList";
import TriggerRulesEditor from "./TriggerRulesEditor";
import { TriggerTypeSelectionPanel } from "./components/TriggerTypeSelectionPanel";
import { useWorkflow } from "./hooks/useWorkflow";
import HButton from "../../components/custom/input/button/Button";
import { Trigger, type TriggerType } from "./nodes/TriggerNode";
import "./triggerRules.css";

const TriggerRules: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const idFromUrl = searchParams.get("id");
    const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowRecord | null | "new">(null);
    const [triggerTypeForNew, setTriggerTypeForNew] = useState<TriggerType | null>(null);

    const activeWorkflowId = selectedWorkflow !== null && selectedWorkflow !== "new"
        ? selectedWorkflow.workflowId
        : null;
    const { workflow: serverWorkflow, isLoading: workflowLoading } = useWorkflow(activeWorkflowId);

    useEffect(() => {
        if (idFromUrl === "new") {
            setSelectedWorkflow("new");
        }
    }, [idFromUrl]);

    const handleWorkflowClick = (workflow: WorkflowRecord) => {
        setSelectedWorkflow(workflow);
        setTriggerTypeForNew(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("id", workflow.workflowId);
            return next;
        });
    };

    const handleNewWorkflow = () => {
        setSelectedWorkflow("new");
        setTriggerTypeForNew(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("id", "new");
            return next;
        });
    };

    const handleBack = () => {
        setSelectedWorkflow(null);
        setTriggerTypeForNew(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("id");
            return next;
        });
    };

    const handleTriggerTypeConfirm = (triggerType: TriggerType) => {
        setTriggerTypeForNew(triggerType);
    };

    const handleTriggerTypeCancel = () => {
        setSelectedWorkflow(null);
        setTriggerTypeForNew(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("id");
            return next;
        });
    };

    const getTriggerType = (): TriggerType => {
        if (selectedWorkflow !== null && selectedWorkflow !== "new") {
            return selectedWorkflow.direction === "Outgoing" ? Trigger.Message.Outbound : Trigger.Message.Inbound;
        }
        return triggerTypeForNew ?? Trigger.Message.Inbound;
    };

    if (selectedWorkflow === null) {
        return (
            <TriggerRulesList
                onWorkflowClick={handleWorkflowClick}
                onNewWorkflow={handleNewWorkflow}
                initialWorkflowId={idFromUrl}
            />
        );
    }

    if (selectedWorkflow === "new" && triggerTypeForNew === null) {
        return (
            <Flex className="trigger-rules">
                <div className="trigger-rules__main">
                    <Flex className="py-4 px-3 hs-border-bottom-light" justify="space-between" align="center">
                        <Flex align="center" gap={12}>
                            <Input
                                value="New Workflow"
                                disabled
                                className="trigger-rules__rule-name-input"
                                suffix={<EditOutlined className="trigger-rules__edit-icon" aria-hidden />}
                                aria-label="Workflow name"
                            />
                        </Flex>
                        <Flex gap={12} wrap="wrap">
                            <HButton
                                size="s"
                                type="text"
                                buttonClassName="hs-color-BlackRussian"
                                onClick={handleTriggerTypeCancel}
                            >
                                Cancel
                            </HButton>
                            <HButton size="s" type="primary" disabled>
                                Save
                            </HButton>
                        </Flex>
                    </Flex>
                    <div className="trigger-rules__body">
                        <div className="trigger-rules__canvas d-flex justify-content-center align-items-center">
                            <div className="automationCardsSprite trigger-rules__trigger-center-illustration" aria-hidden></div>
                        </div>
                    </div>
                </div>
                <div className="trigger-rules__config-panel">
                    <TriggerTypeSelectionPanel
                        onConfirm={handleTriggerTypeConfirm}
                        onCancel={handleTriggerTypeCancel}
                    />
                </div>
            </Flex>
        );
    }


    if (activeWorkflowId && workflowLoading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ position: "fixed", inset: 0, zIndex: 10 }}
            >
                <div className="actionIconsSprite dropDownLoading" />
            </div>
        );
    }

    return (
        <TriggerRulesEditor
            workflow={selectedWorkflow === "new" ? null : selectedWorkflow}
            serverWorkflow={serverWorkflow}
            onBack={handleBack}
            triggerType={getTriggerType()}
        />
    );
};

export default TriggerRules;
